const MASK = 'TT.MM.JJJJ';

// Helper to detect Safari (excluding Chrome, Edge, etc.)
const isSafari = (): boolean => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// Helper function to check if a digit is allowed at a given position,
// based on the requirements for day (01-31) and month (01-12)
const isAllowedDigit = (pos: number, digit: string, currentValue: string): boolean => {
    // For the day (TT) at positions 0 and 1
    if (pos < 2) {
        if (pos === 0) {
            // First digit must be 0, 1, 2 or 3
            return ['0', '1', '2', '3'].includes(digit);
        } else if (pos === 1) {
            const firstDigit = currentValue.charAt(0);
            if (firstDigit === '0') {
                // "00" is not allowed – second digit must be 1-9
                return /[1-9]/.test(digit);
            } else if (firstDigit === '3') {
                // "3" may only be followed by 0 or 1 (i.e. 30 or 31)
                return ['0', '1'].includes(digit);
            }
            // For "1" or "2" all digits 0-9 are allowed
            return true;
        }
    }
    // For the month (MM) at positions 3 and 4
    if (pos >= 3 && pos < 5) {
        if (pos === 3) {
            // First digit of the month can only be 0 or 1
            return ['0', '1'].includes(digit);
        } else if (pos === 4) {
            const firstDigit = currentValue.charAt(3);
            if (firstDigit === '0') {
                // "00" is invalid, so the second digit must be between 1 and 9
                return /[1-9]/.test(digit);
            } else if (firstDigit === '1') {
                // For "1" only 0, 1 or 2 is allowed as the second digit (10, 11, 12)
                return ['0', '1', '2'].includes(digit);
            }
        }
    }
    return true;
};

const isEditable = (pos: number): boolean => pos !== 2 && pos !== 5;

const getPlaceholderFor = (pos: number): string => {
    if (pos < 2) return 'T';
    if (pos >= 3 && pos < 5) return 'M';
    if (pos >= 6 && pos < 10) return 'J';
    return '';
};

const nextEditable = (pos: number): number => {
    while (pos < 10 && !isEditable(pos)) pos++;
    return pos;
};

const prevEditable = (pos: number): number => {
    while (pos > 0 && !isEditable(pos - 1)) pos--;
    return pos > 0 ? pos - 1 : 0;
};

const clearSelection = (input: HTMLInputElement, start: number, end: number): void => {
    const arr = input.value.split('');
    for (let i = start; i < end; i++) {
        if (isEditable(i)) {
            arr[i] = getPlaceholderFor(i);
        }
    }
    input.value = arr.join('');
};

// Updates the hidden input and validates the date
const updateHiddenInput = (visibleInput: HTMLInputElement, hiddenInput: HTMLInputElement): void => {
    const val = visibleInput.value;
    const day = val.substring(0, 2);
    const month = val.substring(3, 5);
    const year = val.substring(6, 10);
    const isComplete = /^\d{2}$/.test(day) && /^\d{2}$/.test(month) && /^\d{4}$/.test(year);

    if (isComplete) {
        const d = parseInt(day, 10);
        const m = parseInt(month, 10);
        const y = parseInt(year, 10);

        hiddenInput.value = `${y}-${('0' + m).slice(-2)}-${('0' + d).slice(-2)}`;
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
        hiddenInput.value = '';
    }

    // Notify listeners (e.g. syncSameFieldsInForm) about the new hidden value
    hiddenInput.dispatchEvent(new Event('birthday:update', { bubbles: true }));
};

const handleDigit = (visibleInput: HTMLInputElement, hiddenInput: HTMLInputElement, digit: string): void => {
    let pos = visibleInput.selectionStart ?? 0;
    if (visibleInput.selectionStart !== visibleInput.selectionEnd) {
        clearSelection(visibleInput, pos, visibleInput.selectionEnd ?? 0);
        pos = visibleInput.selectionStart ?? 0;
    }
    while (pos < 10 && !isEditable(pos)) pos++;
    if (pos >= 10) return;

    // Here we check the ranges for day and month:
    if (!isAllowedDigit(pos, digit, visibleInput.value)) return; // Block input if the digit is not allowed for the current range

    const arr = visibleInput.value.split('');
    arr[pos] = digit;
    visibleInput.value = arr.join('');
    pos = nextEditable(pos + 1);
    visibleInput.setSelectionRange(pos, pos);
    updateHiddenInput(visibleInput, hiddenInput);
};

const handleBackspace = (visibleInput: HTMLInputElement, hiddenInput: HTMLInputElement): void => {
    const pos = visibleInput.selectionStart ?? 0;
    if (visibleInput.selectionStart !== visibleInput.selectionEnd) {
        clearSelection(visibleInput, pos, visibleInput.selectionEnd ?? 0);
        visibleInput.setSelectionRange(visibleInput.selectionStart, visibleInput.selectionStart);
        updateHiddenInput(visibleInput, hiddenInput);

        return;
    }

    const newPos = prevEditable(pos);
    if (newPos < 0) return;
    const arr = visibleInput.value.split('');
    arr[newPos] = getPlaceholderFor(newPos);
    visibleInput.value = arr.join('');
    visibleInput.setSelectionRange(newPos, newPos);
    updateHiddenInput(visibleInput, hiddenInput);
};

// Writes the pasted digits into the mask, applying the same range checks as typing
const pasteDigits = (visibleInput: HTMLInputElement, hiddenInput: HTMLInputElement, text: string): void => {
    const digits = text.replace(/\D/g, '');
    let pos = visibleInput.selectionStart ?? 0;
    const arr = visibleInput.value.split('');

    for (let i = 0; i < digits.length && pos < 10; i++) {
        while (pos < 10 && !isEditable(pos)) pos++;
        if (pos >= 10) break;
        if (!isAllowedDigit(pos, digits[i], visibleInput.value)) continue; // Skip digits that are not allowed in this range
        arr[pos] = digits[i];
        pos = nextEditable(pos + 1);
    }

    visibleInput.value = arr.join('');
    visibleInput.setSelectionRange(pos, pos);
    updateHiddenInput(visibleInput, hiddenInput);
};

// Registers all event listeners for a visible input field
const addEventListenersInputMask = (visibleInput: HTMLInputElement, hiddenInput: HTMLInputElement): void => {
    let lastSafeValue = visibleInput.value || MASK;
    let lastSafeSel = { start: 0, end: 0 };
    let blockedByBeforeInput = false;

    const rememberSafe = (): void => {
        lastSafeValue = visibleInput.value;
        lastSafeSel = {
            start: visibleInput.selectionStart ?? 0,
            end: visibleInput.selectionEnd ?? 0
        };
    };

    visibleInput.addEventListener('keydown', e => {
        // Allows control keys (Ctrl/Meta) and special keys such as arrow keys, backspace, etc.
        // If no Ctrl/Meta is pressed and the key is a single character,
        // it is checked whether it is a number - otherwise block.
        if (!e.ctrlKey && !e.metaKey && e.key.length === 1 && !/^\d$/.test(e.key)) {
            e.preventDefault();
            return;
        }

        // Handling the number key
        if (/^\d$/.test(e.key)) {
            e.preventDefault();
            handleDigit(visibleInput, hiddenInput, e.key);

            return;
        }

        // Backspace: Deletes the character to the left of the caret
        if (e.key === 'Backspace') {
            e.preventDefault();
            handleBackspace(visibleInput, hiddenInput);

            return;
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();

            return;
        }

        // Arrow keys: For fixed characters (dots), position the caret correctly
        if (e.key === 'ArrowLeft') {
            setTimeout(() => {
                const newPos = visibleInput.selectionStart ?? 0;
                if (newPos === 2 || newPos === 5) {
                    visibleInput.setSelectionRange(newPos - 1, newPos - 1);
                }
            }, 0);
        }
        if (e.key === 'ArrowRight') {
            setTimeout(() => {
                const newPos = visibleInput.selectionStart ?? 0;
                if (newPos === 2 || newPos === 5) {
                    visibleInput.setSelectionRange(newPos + 1, newPos + 1);
                }
            }, 0);
        }
    });

    visibleInput.addEventListener('beforeinput', e => {
        const type = e.inputType || '';
        const data = e.data;
        const isAllowedInsert = type === 'insertText' && typeof data === 'string' && /^\d$/.test(data);

        // Consider additional types & composition (IME = Input Method Editor = App keyboard)
        const looksLikeIME =
            e.isComposing ||
            type.startsWith('insertComposition') ||
            type === 'insertFromSuggestion' ||
            type === 'insertFromComposition' ||
            type === 'insertReplacementText';

        // Allow backspace separately
        if (type === 'deleteContentBackward') {
            if (e.cancelable) e.preventDefault();
            handleBackspace(visibleInput, hiddenInput);
            return;
        }

        // Block IME Enter/line breaks and explicitly the apostrophe
        if (type === 'insertLineBreak' || type === 'insertParagraph') {
            e.preventDefault();

            return;
        }

        if (isAllowedInsert) {
            e.preventDefault();
            handleDigit(visibleInput, hiddenInput, data);
            rememberSafe();

            return;
        }

        if (type === 'insertFromPaste') {
            if (e.cancelable) e.preventDefault();

            pasteDigits(visibleInput, hiddenInput, e.dataTransfer?.getData('text') ?? '');
            rememberSafe();

            return;
        }

        // Block all non-numerical elements / composition
        if (looksLikeIME || !isAllowedInsert) {
            if (e.cancelable) {
                e.preventDefault();
            } else {
                // realize that we need to roll back immediately in the 'input'
                blockedByBeforeInput = true;
            }
        }
    });

    // Additionally: For each input event we filter out all forbidden characters.
    visibleInput.addEventListener('input', () => {
        // Allow only digits in editable positions
        const valueInput = visibleInput.value;
        const bad =
            Array.from(valueInput).some((char, idx) => {
                if (!isEditable(idx)) return false;
                // Allowed: Number or placeholder
                return !(/\d/.test(char) || char === getPlaceholderFor(idx));
            });

        if (blockedByBeforeInput || bad) {
            visibleInput.value = lastSafeValue;
            visibleInput.setSelectionRange(lastSafeSel.start, lastSafeSel.end);
            blockedByBeforeInput = false;

            return;
        }

        // Forbidden sign cleanup
        const forbiddenCharsRegex = /[',°^`´]/g;
        if (forbiddenCharsRegex.test(visibleInput.value)) {
            visibleInput.value = visibleInput.value.replace(forbiddenCharsRegex, '');
        }

        updateHiddenInput(visibleInput, hiddenInput);
        rememberSafe();
    });

    // Paste event: Copies only the digits from the clipboard and applies the same range checks.
    visibleInput.addEventListener('paste', e => {
        e.preventDefault();
        pasteDigits(visibleInput, hiddenInput, e.clipboardData?.getData('text') ?? '');
    });

    // Prevent drag & drop
    visibleInput.addEventListener('dragover', e => e.preventDefault());
    visibleInput.addEventListener('drop', e => e.preventDefault());

    // Click event: On initial click, jump to the beginning.
    visibleInput.addEventListener('click', () => {
        // On first click, always move the caret to the start.
        if (!visibleInput.dataset.initialClickHandled) {
            visibleInput.setSelectionRange(0, 0);
            visibleInput.dataset.initialClickHandled = 'true';
            return;
        }
        // Then follow the original logic if the date is incomplete.
        const completeRegex = /^\d{2}\.\d{2}\.\d{4}$/;
        if (!completeRegex.test(visibleInput.value)) {
            let nextPlaceholderIndex = null;
            for (let i = 0; i < visibleInput.value.length; i++) {
                if (isEditable(i) && !/\d/.test(visibleInput.value[i])) {
                    nextPlaceholderIndex = i;
                    break;
                }
            }
            if (nextPlaceholderIndex !== null) {
                visibleInput.setSelectionRange(nextPlaceholderIndex, nextPlaceholderIndex);
            }
        } else {
            let pos = visibleInput.selectionStart ?? 0;
            if (pos === 2 || pos === 5) {
                pos = nextEditable(pos);
                visibleInput.setSelectionRange(pos, pos);
            }
        }
    });

    // Focus: Set mask when field is empty
    visibleInput.addEventListener('focus', () => {
        if (!visibleInput.value) {
            visibleInput.value = MASK;
            if (isSafari()) {
                setTimeout(() => {
                    visibleInput.setSelectionRange(0, 0);
                }, 10);
            }
            // Also ensure caret is at the start on focus.
            visibleInput.setSelectionRange(0, 0);
        }
    });

    // Blur: Validate when leaving the field
    visibleInput.addEventListener('blur', () => {
        updateHiddenInput(visibleInput, hiddenInput);

        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        hiddenInput.dispatchEvent(new Event('blur', { bubbles: true }));

        if (!/\d/.test(visibleInput.value)) {
            visibleInput.value = '';
        }
    });
};

// parses ISO string and fills the mask field
const fillMaskFromInitialValue = (visibleInput: HTMLInputElement, hiddenInput: HTMLInputElement): void => {
    const iso = hiddenInput.value; // expected "YYYY-MM-DD"
    if (!iso) return;

    const [year, month, day] = iso.split('-');
    if (year && month && day) {
        visibleInput.value = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
    }
};

// Initializes a single visible input field
const initBirthdayInput = (visibleInput: HTMLInputElement): void => {
    const hiddenInput = visibleInput.parentNode?.querySelector<HTMLInputElement>('.js-form-validation');
    if (!hiddenInput || visibleInput.dataset.birthdayMask) return;

    // Prevent double initialization (e.g. init on document and again on AJAX-loaded content)
    visibleInput.dataset.birthdayMask = 'true';

    // Set placeholder
    visibleInput.placeholder = MASK;
    hiddenInput.placeholder = MASK;

    fillMaskFromInitialValue(visibleInput, hiddenInput);

    addEventListenersInputMask(visibleInput, hiddenInput);
};

// Initializes all birthday inputs within root (defaults to document, pass a container for AJAX-loaded content)
const init = (root: ParentNode = document): void => {
    root.querySelectorAll<HTMLInputElement>('.js-form-control-birthday').forEach(initBirthdayInput);
};

export { init };
